
import { Component, Input, Optional } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { TranslatePipe, TranslateService} from '@ngx-translate/core';
import { CommunicationPreferencesPopupComponent } from 'src/app/Shared/components/communication-preferences-popup/communication-preferences-popup.component';
import { StorageService } from 'src/app/Core/_services/storage/storage.service';
import { CommunicationsPreferencesService } from 'src/app/Shared/_services/communication/communications-preferences.service';
import { ToastService } from 'src/app/Shared/services/toast/toast.service';
import { LocaleService } from '../../services/locale/locale.service';

interface CommunicationPreference {
  id: number;
  user_id: number;
  communication_type_id: number;
  communication_channel_id: number;
  communication_channel: {
    id: number;
    name: string;
  };
  communication_type: {
    id: number;
    name: string;
  };
  opted_in: boolean | null;
  action_at: string | null;
  created_at: string;
  updated_at: string;
}

interface CommunicationType {
  id: number;
  name: string;
  description?: string;
}

interface CommunicationChannel {
  id: number;
  name: string;
  icon?: string;
}

interface MatrixCell {
  preferenceId: number;
  typeId: number;
  channelId: number;
  optedIn: boolean | null;
}

interface DialogData {
  CommunicationPreferences: CommunicationPreference[];
  isClose: boolean;
}
@Component({
    selector: 'app-communication-preferences-matrix',
        standalone: true,
imports: [
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatButtonModule,
    TranslatePipe
],
    templateUrl: './communication-preferences-matrix.component.html',
    styleUrl: './communication-preferences-matrix.component.scss'
})
export class CommunicationPreferencesMatrixComponent {
  preferencesForm!: FormGroup;
  matrix: { [key: string]: MatrixCell } = {};
  originalPreferences: { [key: string]: boolean | null } = {};
  isSaving = false;
  types: CommunicationType[] = [];
  channels: CommunicationChannel[] = [];
  user: any;

  currentLang: any;
  @Input() data!: DialogData;
  constructor(
    private fb: FormBuilder,
    private communicationsPreferencesService: CommunicationsPreferencesService,
    private toaster: ToastService,
    private storage: StorageService,
    private translate: TranslateService,
    @Optional()
    private dialogRef?: MatDialogRef<CommunicationPreferencesPopupComponent>
  ) {
    this.preferencesForm = this.fb.group({});
  }

  ngOnInit() {
    this.extractTypesAndChannels();
    this.initializeMatrix();
    this.createFormControls();
    this.user = this.storage?.userData;
  }
  private extractTypesAndChannels() {
    // Extract unique types and channels from the preferences data
    const typesMap = new Map<number, CommunicationType>();
    const channelsMap = new Map<number, CommunicationChannel>();
    this.data?.CommunicationPreferences.forEach((pref) => {
      // Extract communication types
      if (
        pref.communication_type &&
        !typesMap.has(pref.communication_type.id)
      ) {
        typesMap.set(pref.communication_type.id, {
          id: pref.communication_type.id,
          name: this.translateCommunicationType(pref.communication_type.name),
        });
      }

      // Extract communication channels
      if (
        pref.communication_channel &&
        !channelsMap.has(pref.communication_channel.id)
      ) {
        channelsMap.set(pref.communication_channel.id, {
          id: pref.communication_channel.id,
          name: this.translateCommunicationChannel(pref.communication_channel.name),
          icon: this.getChannelIcon(pref.communication_channel.name),
        });
      }
    });

    this.types = Array.from(typesMap.values()).sort((a, b) => a.id - b.id);
    this.channels = Array.from(channelsMap.values()).sort(
      (a, b) => a.id - b.id
    );
  }

  private formatDisplayName(name: string): string {
    // Convert snake_case to readable format
    return name
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private translateCommunicationType(type: string): string {
    if (!type) return '';
    const key = `communication_preferences.type_${type.toLowerCase()}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : this.formatDisplayName(type);
  }

  private translateCommunicationChannel(channel: string): string {
    if (!channel) return '';
    const key = `communication_preferences.channel_${channel.toLowerCase()}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : this.formatDisplayName(channel);
  }

  private getChannelIcon(channelName: string): string {
    const icons: { [key: string]: string } = {
      email: 'envelope',
      whatsapp: 'whatsapp',
      sms: 'chat-dots',
      push: 'bell',
    };
    return icons[channelName] || 'notifications';
  }

  private initializeMatrix() {
    // Create matrix from preferences data
    this.data.CommunicationPreferences.forEach((pref) => {
      const key = this.getMatrixKey(
        pref.communication_type_id,
        pref.communication_channel_id
      );
      this.matrix[key] = {
        preferenceId: pref.id,
        typeId: pref.communication_type_id,
        channelId: pref.communication_channel_id,
        optedIn: pref.opted_in,
      };

      // Store original values for change detection
      this.originalPreferences[key] = pref.opted_in;
    });
  }

  private createFormControls() {
    const controls: { [key: string]: FormControl } = {};

    this.types.forEach((type) => {
      this.channels.forEach((channel) => {
        const controlName = this.getControlName(type.id, channel.id);
        const matrixKey = this.getMatrixKey(type.id, channel.id);
        const preference = this.matrix[matrixKey];

        // Handle null as false for checkbox display, but track original state
        const checkboxValue = preference?.optedIn === true;
        controls[controlName] = new FormControl(checkboxValue);
      });
    });

    this.preferencesForm = this.fb.group(controls);
  }

  getControlName(typeId: number, channelId: number): string {
    return `type_${typeId}_channel_${channelId}`;
  }

  private getMatrixKey(typeId: number, channelId: number): string {
    return `${typeId}_${channelId}`;
  }

  isOddRow(typeId: number): boolean {
    return this.types.findIndex((t) => t.id === typeId) % 2 === 1;
  }

  onPreferenceChange(typeId: number, channelId: number, event: any) {
    const key = this.getMatrixKey(typeId, channelId);
    if (this.matrix[key]) {
      this.matrix[key].optedIn = event.checked;
    }
  }

  selectAll() {
    this.types.forEach((type) => {
      this.channels.forEach((channel) => {
        const controlName = this.getControlName(type.id, channel.id);
        this.preferencesForm.get(controlName)?.setValue(true);

        const key = this.getMatrixKey(type.id, channel.id);
        if (this.matrix[key]) {
          this.matrix[key].optedIn = true;
        }
      });
    });
  }

  deselectAll() {
    this.types.forEach((type) => {
      this.channels.forEach((channel) => {
        const controlName = this.getControlName(type.id, channel.id);
        this.preferencesForm.get(controlName)?.setValue(false);

        const key = this.getMatrixKey(type.id, channel.id);
        if (this.matrix[key]) {
          this.matrix[key].optedIn = false;
        }
      });
    });
  }

  hasChanges(): boolean {
    return Object.keys(this.matrix).some((key) => {
      const current = this.matrix[key].optedIn;
      const original = this.originalPreferences[key];
      return current !== original;
    });
  }

  // Get only changed preferences (for UI feedback)
  getChangedPreferences(): any[] {
    const changes: any[] = [];

    Object.keys(this.matrix).forEach((key) => {
      const preference = this.matrix[key];
      const original = this.originalPreferences[key];

      if (preference.optedIn !== original) {
        changes.push({
          communication_type_id: preference.typeId,
          communication_channel_id: preference.channelId,
          opted_in: preference.optedIn || false, // Convert null to false for API
        });
      }
    });

    return changes;
  }

  // NEW METHOD: Get ALL preferences with current values (including false values)
  getAllPreferences(): any[] {
    const allPreferences: any[] = [];

    Object.keys(this.matrix).forEach((key) => {
      const preference = this.matrix[key];
      allPreferences.push({
        communication_type_id: preference.typeId,
        communication_channel_id: preference.channelId,
        opted_in: preference.optedIn === null ? false : preference.optedIn, // Convert null to false
      });
    });

    return allPreferences;
  }

  // UPDATED: Save method to send ALL preferences
  async savePreferences() {
    this.isSaving = true;
    this.toaster.clear();
    // Send ALL preferences with current values (including false values)
    const allPreferencesPayload = {
      preferences: this.getAllPreferences(), // Changed from getChangedPreferences()
    };

    this.communicationsPreferencesService
      .changeCommunicationPreferences(allPreferencesPayload)
      .subscribe({
        next: (response) => {
          this.isSaving = false;
          this.toaster.showSuccess(response?.message);

          // Update original preferences to reflect saved state
          Object.keys(this.matrix).forEach((key) => {
            this.originalPreferences[key] = this.matrix[key].optedIn;
          });
          const updatedUser = {
            ...this.user,
            has_pending_communication_preferences: false,
          };

          // Store user data
          this.storage.userData = JSON.stringify(updatedUser);
          if (this.dialogRef) {
            this.dialogRef.close({
              success: true,
              changes: this.getChangedPreferences(), // Only changes for UI feedback
              allPreferences: this.getAllPreferences(), // All preferences sent to API
              message: response?.message,
            });
          }
        },
        error: (err) => {
          this.isSaving = false;
          console.error('Error saving preferences:', err);
          this.toaster.showError(
            err?.error?.error?.[0] || 'Failed to save preferences'
          );
        },
      });
  }

  // UTILITY METHODS for better UX

  // Track by functions for performance
  trackByTypeId(index: number, type: CommunicationType): number {
    return type.id;
  }

  trackByChannelId(index: number, channel: CommunicationChannel): number {
    return channel.id;
  }

  // Get count of changes for UI
  getChangesCount(): number {
    return this.getChangedPreferences().length;
  }

  // Get summary for UI display
  getPreferencesSummary(): {
    total: number;
    enabled: number;
    disabled: number;
    notSet: number;
  } {
    const total = Object.keys(this.matrix).length;
    const enabled = Object.values(this.matrix).filter(
      (p) => p.optedIn === true
    ).length;
    const disabled = Object.values(this.matrix).filter(
      (p) => p.optedIn === false
    ).length;
    const notSet = Object.values(this.matrix).filter(
      (p) => p.optedIn === null
    ).length;

    return { total, enabled, disabled, notSet };
  }
}
